/* * Copyright 2017-2017 Amazon.com, Inc. or its affiliates. All Rights
Reserved. * * Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with * the License. A copy of the
License is located at * * http://aws.amazon.com/apache2.0/ * * or in the
"license" file accompanying this file. This file is distributed on an "AS IS"
BASIS, WITHOUT WARRANTIES OR * CONDITIONS OF ANY KIND, either express or
implied. See the License for the specific language governing permissions * and
limitations under the License. */

<template>
  <ValidationObserver ref="obs" v-slot="{ invalid, validated }">
    <v-card
      max-width="600"
      color="transparent"
      :elevation="0"
      v-bind:data-test="auth.setMFAComp.section"
    >
      <v-card-title v-if="!displayTotpSetup">
        <span class="title font-weight-regular">{{
          $Amplify.I18n.get(options.header)
        }}</span>
      </v-card-title>
      <v-card-title v-if="!displayTotpSetup">
        <div class="subtitle-2 font-weight-regular">
          <v-alert text dense color="info" border="left">
            {{ $Amplify.I18n.get(options.mfaDescription) }}
          </v-alert>
        </div>
      </v-card-title>

      <v-card-title
        v-if="displayTotpSetup"
        v-bind:data-test="auth.setMFAComp.headerSection"
      >
        <span class="title font-weight-regular">
          {{ $Amplify.I18n.get("Verify Authentication Token") }}</span
        >

        <div class="subtitle-1 font-weight-light">
          {{ $Amplify.I18n.get(options.tokenInstructions) }}
        </div>
      </v-card-title>
      <v-card-text>
        <v-alert
          v-if="error"
          v-model="alert"
          prominent
          dense
          dismissible
          type="error"
          transition="scale-transition"
        >
          <v-layout align-center wrap>
            <div>
              {{ error }}
            </div>
          </v-layout>
        </v-alert>
      </v-card-text>
      <v-card-text
        v-bind:data-test="auth.setMFAComp.bodySection"
        v-if="!displayTotpSetup"
        style="padding-top:0px; padding-bottom:0px"
      >
        <form>
          <ValidationProvider
            name="code"
            rules="required"
            v-slot="{ errors, valid }"
          >
            <v-radio-group
              v-model="mfaPreference"
              style="padding:0px; margin:0px"
            >
              <v-radio
                v-if="options.mfaTypes.includes('SMS')"
                name="mfaPreference"
                :label="`${$Amplify.I18n.get(options.smsDescription)}`"
                value="SMS"
                v-bind:data-test="auth.setMFAComp.smsInput"
              ></v-radio>
              <!-- <v-radio
                v-if="options.mfaTypes.includes('TOTP')"
                name="mfaPreference"
                :label="`${$Amplify.I18n.get(options.totpDescription)}`"
                value="TOTP"
                v-bind:data-test="auth.setMFAComp.totpInput"
              ></v-radio> -->
              <v-radio
                v-if="options.mfaTypes.includes('None')"
                name="mfaPreference"
                :label="`${$Amplify.I18n.get(options.noMfaDescription)}`"
                value="NOMFA"
                v-bind:data-test="auth.setMFAComp.noMfaInput"
              ></v-radio>
            </v-radio-group>
          </ValidationProvider>
        </form>
      </v-card-text>
      <v-card-text
        v-if="displayTotpSetup"
        style="padding-top:0px; padding-bottom:0px"
      >
        <v-layout align-center justify-center>
          <span class="text-center"
            ><qrcode-vue
              v-bind:class="amplifyUI.totpQrcode"
              :value="token"
              :size="150"
              level="H"
            ></qrcode-vue>
            <form>
              <ValidationProvider
                name="code"
                rules="required"
                v-slot="{ errors, valid }"
              >
                <v-text-field
                  v-validate="'required'"
                  v-model="code"
                  :placeholder="$Amplify.I18n.get('Verification Code')"
                  :label="`${$Amplify.I18n.get('Verification Code')} *`"
                  v-on:keyup.enter="setMFA"
                  autofocus
                  v-bind:data-test="auth.setMFAComp.verificationCodeInput"
                  :error-messages="errors"
                  :success="valid"
                ></v-text-field>
              </ValidationProvider></form
          ></span>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-container fluid ma-0 pa-0>
          <v-layout wrap>
            <v-flex pa-2 order-xl1 order-lg1 order-md1 order-sm1 order-xs2>
              <span v-if="!options.hideCancelButton">
                <a
                  v-on:click="cancel"
                  v-bind:data-test="auth.setMFAComp.cancelButton"
                  >{{ $Amplify.I18n.get("Cancel") }}</a
                >
              </span>
            </v-flex>

            <v-flex pa-2 order-xl2 order-lg2 order-md2 order-sm2 order-xs1>
              <v-btn
                block
                v-on:click="setMFA"
                v-if="!displayTotpSetup"
                v-bind:data-test="auth.setMFAComp.setMfaButton"
                color="primary"
                :disabled="invalid || !validated || loadingBtn"
                :loading="loadingBtn"
              >
                {{ $Amplify.I18n.get("Set MFA") }}
                <template v-slot:loader>
                  <span>{{ $Amplify.I18n.get("Wait...") }}</span>
                </template>
              </v-btn>

              <v-btn
                id="verify"
                block
                v-on:click="verifyTotpToken"
                v-if="displayTotpSetup"
                v-bind:data-test="auth.setMFAComp.verifyTotpTokenButton"
                color="primary"
                :disabled="invalid || !validated || loadingBtn"
                :loading="loadingBtn"
              >
                {{ $Amplify.I18n.get("Set MFA") }}
                <template v-slot:loader>
                  <span>{{ $Amplify.I18n.get("Wait...") }}</span>
                </template>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-actions>
    </v-card>
  </ValidationObserver>
</template>

<script>
// import Auth from '@aws-amplify/auth';
import { AmplifyEventBus } from "aws-amplify-vue";
import * as AmplifyUI from "@aws-amplify/ui";
import QrcodeVue from "qrcode.vue";
import { auth } from "aws-amplify-vue/src/assets/data-test-attributes";
import { ValidationObserver, ValidationProvider } from "vee-validate";

export default {
  name: "SetMfa",
  components: {
    ValidationProvider,
    ValidationObserver,
    QrcodeVue
  },
  props: ["mfaConfig"],
  data() {
    return {
      user: null,
      mfaPreference: null,
      code: null,
      token: "",
      error: "",
      displayTotpSetup: false,
      amplifyUI: AmplifyUI,
      auth,
      logger: {},
      alert: false,
      loadingBtn: false,
      more: false
    };
  },
  mounted() {
    this.logger = new this.$Amplify.Logger(this.$options.name);
    this.setUser();
  },
  computed: {
    options() {
      const defaults = {
        header: "Multifactor Authentication Preference",
        mfaDescription:
          "The Multi-Factor Authentication (MFA) adds an extra layer of protection on top of your user name and password.",
        tokenInstructions:
          "Scan the QR Code with your phone camera or authentication app to get the MFA code.",
        smsDescription:
          "SMS text messaging (receive a code on your mobile device)",
        totpDescription:
          "One-time password (use a QR code and MFA app to save a token on your mobile device)",
        noMfaDescription: "Do not enable MFA",
        mfaTypes: [],
        hideCancelButton: false
      };
      return Object.assign(defaults, this.mfaConfig || {});
    }
  },
  watch: {
    mfaPreference(value) {
      if (value === "TOTP") {
        this.setup();
      }
    }
  },
  methods: {
    setup() {
      this.$Amplify.Auth.setupTOTP(this.user)
        .then(data => {
          this.logger.info("setTOTP success");
          this.token =
            "otpauth://totp/AWSCognito:" +
            this.user.username +
            "?secret=" +
            data +
            "&issuer=AWSCognito";
        })
        .catch(e => this.setError(e));
    },
    setUser: async function() {
      await this.$Amplify.Auth.currentAuthenticatedUser()
        .then(user => {
          this.logger.info("currentAuthenticatedUser success");
          if (user) {
            console.log(user);
            this.user = user;
            switch (user.preferredMFA) {
              case "NOMFA":
                this.mfaPreference = user.preferredMFA;
                break;
              case "SMS_MFA":
                this.mfaPreference = "SMS";
                break;
            }
          } else {
            this.user = null;
          }
          return this.user;
        })
        .catch(e => {
          this.user = null;
          this.setError(e);
          return this.user;
        });
    },
    setMFA: function() {
      this.loadingBtn = true;
      this.$Amplify.Auth.setPreferredMFA(this.user, this.mfaPreference)
        .then(data => {
          this.loadingBtn = false;
          AmplifyEventBus.$emit("authState", "signedIn");
          this.$destroy();
        })
        .catch(e => {
          this.loadingBtn = false;
          if (e.message == "User has not verified software token mfa") {
            return (this.displayTotpSetup = true);
          }
          this.setError(e);
        });
    },
    verifyTotpToken() {
      this.$Amplify.Auth.verifyTotpToken(this.user, this.code)
        .then(() => {
          this.logger.info("verifyTotpToken success");
          this.setMFA();
        })
        .catch(e => this.setError(e));
    },
    setError: function(e) {
      this.loadingBtn = false;
      this.alert = true;
      this.error = this.$Amplify.I18n.get(e.message || e);
      this.logger.error(this.error);
    },
    cancel: function() {
      return this.options.cancelHandler ? this.options.cancelHandler() : null;
    }
  }
};
</script>
